/**
 * @file api-customers.ts
 * @description API-клиент клиентов / юр.лиц / адресов / согласий / блокировок
 * / запросов на удаление (phase08).
 */

import { apiFetch } from "./api-client";
import { groupConsents } from "./customer-consents";

export type CustomerType = "INDIVIDUAL" | "LEGAL";
export type AddressKind = "SHIPPING" | "BILLING" | "PICKUP";
export type ConsentType =
  | "PD_PROCESSING"
  | "MARKETING_EMAIL"
  | "MARKETING_SMS"
  | "COOKIE"
  // v0.19.6 — типы согласий Customer Portal (CustomerConsent, origin=PORTAL)
  | "PERSONAL_DATA"
  | "MARKETING_TELEGRAM"
  | "MARKETING_CHAT"
  | "CHAT_HISTORY";
export type ConsentSource =
  | "REGISTRATION"
  | "CHECKOUT"
  | "SETTINGS"
  | "IMPORT"
  // v0.19.6 — согласие из ЛК (таблица CustomerConsent)
  | "PORTAL";
export type CustomerBlockScope = "ORDERS" | "LOGIN" | "PAYMENT";
export type DataDeletionStatus = "PENDING" | "COMPLETED" | "REJECTED";

export interface CustomerDto {
  id: string;
  type: CustomerType;
  legalEntityId: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  ordersCount: number;
  lifetimeValueGross: string;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  acquisitionSource: string | null;
  acquisitionUtm: unknown;
  acquisitionErid: string | null;
  riskScore: number;
  isActive: boolean;
  anonymizedAt: string | null;
  blockedScopes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CustomerListResult {
  items: CustomerDto[];
  total: number;
}

export interface CreateCustomerPayload {
  type?: CustomerType;
  legalEntityId?: string;
  displayName?: string;
  email?: string;
  phone?: string;
  acquisitionSource?: string;
  acquisitionErid?: string;
  acquisitionUtm?: Record<string, unknown>;
}

export interface UpdateCustomerPayload {
  type?: CustomerType;
  legalEntityId?: string | null;
  displayName?: string;
  email?: string | null;
  phone?: string | null;
  acquisitionSource?: string;
  acquisitionErid?: string | null;
  acquisitionUtm?: Record<string, unknown> | null;
  riskScore?: number;
}

export interface LegalEntityDto {
  id: string;
  displayName: string;
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  legalAddress: string;
  bankDetails: unknown;
  contactPhone: string | null;
  contactEmail: string | null;
  notes: string | null;
  isActive: boolean;
  dadataSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AddressDto {
  id: string;
  customerId: string;
  kind: AddressKind;
  displayName: string | null;
  fullAddress: string;
  country: string;
  region: string | null;
  city: string | null;
  street: string | null;
  building: string | null;
  apartment: string | null;
  postalCode: string | null;
  fiasId: string | null;
  kladrId: string | null;
  geoLat: string | null;
  geoLon: string | null;
  isDefault: boolean;
  notes: string | null;
  dadataSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConsentRecordDto {
  id: string;
  customerId: string;
  userId?: string | null;
  consentType: ConsentType;
  policyVersionId: string | null;
  acceptedAt: string;
  revokedAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  source: ConsentSource;
  /** v0.19.6 — источник записи: CRM (ConsentRecord) или PORTAL (ЛК). */
  origin?: "CRM" | "PORTAL";
  /**
   * phase19.16, партия «Юридические документы» (шаг Б, uiux-legal-steps-b-c
   * §3): редакция документа, с которым дано согласие. `null` — не записана
   * (согласие до ввода редакций или рассылка без своего документа);
   * `undefined` — сервер поле ещё не отдаёт.
   */
  legalRevision?: ConsentLegalRevisionDto | null;
  /** Источник словами с сервера («при оформлении заказа»); нет — подпись кабинета. */
  sourceLabel?: string;
  /** Заказ, при котором дано согласие (`ConsentRecord.orderId`). */
  order?: { id: string; number: string } | null;
  /** Кто отозвал (менеджер или клиент в ЛК); null/undefined — не известно. */
  revokedByName?: string | null;
}

/** Редакция документа согласия (`legalRevision` записи согласия и `pdConsent` заказа). */
export interface ConsentLegalRevisionDto {
  /** Название документа из редакции: «Согласие на обработку персональных данных». */
  documentTitle: string;
  slug: string;
  number: number;
  publishedAt: string;
  /** Страница редакции на сайте: `/legal/<адрес>/revisions/<N>`. */
  href: string;
  /** Редакцию определили переносом по дате согласия (M2). */
  byDate: boolean;
}

/**
 * Вид согласия в ответе `GET /admin/customers/:id/consents` (шаг Б): поля
 * последней записи на верхнем уровне (прежняя форма) + вид, «действует» и
 * история вида.
 */
export type ConsentKindDto = ConsentRecordDto & {
  kind: ConsentType;
  active: boolean;
  history: ConsentRecordDto[];
};

/**
 * Согласия по виду в кабинете: последняя запись и вся история вида (новые
 * сверху, последняя — первой; только чтение).
 */
export interface ConsentKindGroupDto {
  kind: ConsentType;
  current: ConsentRecordDto;
  history: ConsentRecordDto[];
  /** Действует ли согласие (с сервера; нет — по `revokedAt` последней записи). */
  active: boolean;
}

export interface CustomerBlockDto {
  id: string;
  customerId: string;
  blockedAt: string;
  blockedById: string;
  reason: string;
  scope: CustomerBlockScope;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedById: string | null;
  revokedReason: string | null;
}

export interface DataDeletionRequestDto {
  id: string;
  customerId: string;
  requestedAt: string;
  processedAt: string | null;
  processedById: string | null;
  status: DataDeletionStatus;
  rejectionReason: string | null;
  requestSource: string | null;
  notes: string | null;
}

export interface PolicyVersionDto {
  id: string;
  shopId: string | null;
  policyKind: string;
  version: string;
  content: string;
  publishedAt: string;
  isCurrent: boolean;
}

// ─── Customers ────────────────────────────────────────────────────────────────

/** phase19.6 — топ покупаемых товаров клиента. */
export interface CustomerTopProductDto {
  productId: string | null;
  name: string;
  quantity: number;
  totalGross: string;
}

/** phase19.6 — сводка ценности клиента. */
export interface CustomerStatsDto {
  ordersTotal: number;
  ordersPaid: number;
  ordersCancelled: number;
  ltvPaid: string;
  avgOrderValue: string;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  topProducts: CustomerTopProductDto[];
}

/** phase19.6 — строка списка заказов клиента. */
export interface CustomerOrderListItemDto {
  id: string;
  number: string;
  createdAt: string;
  stageName: string;
  isCancelled: boolean;
  paymentStatus: "NONE" | "PARTIAL" | "FULL" | "REFUNDED";
  shipmentStatus: string;
  totalGross: string;
  itemsCount: number;
}

export const customers = {
  list: (query?: {
    search?: string;
    type?: CustomerType;
    hasBlocks?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (query?.search) params.set("search", query.search);
    if (query?.type) params.set("type", query.type);
    if (query?.hasBlocks !== undefined) params.set("hasBlocks", String(query.hasBlocks));
    if (query?.limit) params.set("limit", String(query.limit));
    if (query?.offset) params.set("offset", String(query.offset));
    const qs = params.toString();
    return apiFetch<CustomerListResult>(
      `/admin/customers${qs ? `?${qs}` : ""}`,
    );
  },

  findOne: (id: string) => apiFetch<CustomerDto>(`/admin/customers/${id}`),

  /** phase19.6 — сводка ценности клиента (LTV/счётчики/топ-5 товаров). */
  stats: (id: string) => apiFetch<CustomerStatsDto>(`/admin/customers/${id}/stats`),

  /** phase19.6 — заказы клиента (новые сверху, limit/offset). */
  orders: (id: string, query?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (query?.limit) params.set("limit", String(query.limit));
    if (query?.offset !== undefined) params.set("offset", String(query.offset));
    const qs = params.toString();
    return apiFetch<{ items: CustomerOrderListItemDto[]; total: number }>(
      `/admin/customers/${id}/orders${qs ? `?${qs}` : ""}`,
    );
  },

  create: (payload: CreateCustomerPayload) =>
    apiFetch<CustomerDto>("/admin/customers", { method: "POST", json: payload }),

  update: (id: string, payload: UpdateCustomerPayload) =>
    apiFetch<CustomerDto>(`/admin/customers/${id}`, {
      method: "PATCH",
      json: payload,
    }),

  anonymize: (id: string) =>
    apiFetch<CustomerDto>(`/admin/customers/${id}/anonymize`, { method: "POST" }),

  mergeInto: (sourceId: string, targetId: string) =>
    apiFetch<CustomerDto>(
      `/admin/customers/${sourceId}/merge-into/${targetId}`,
      { method: "POST" },
    ),
};

// ─── Customer ⟷ LegalEntity links (phase10.6.B2B) ────────────────────────────

export type AdminTaxSystem = "OSNO" | "USN6" | "USN15" | "PSN" | "NPD" | "ESHN";

export interface CustomerBankAccountDto {
  id: string;
  legalEntityId: string;
  accountNumber: string;
  bik: string;
  bankName: string;
  correspondentAccount: string;
  isDefault: boolean;
  isActive: boolean;
  displayName: string | null;
  notes: string | null;
  createdAt: string;
}

export interface CustomerLegalEntityLinkDto {
  linkId: string;
  role: "OWNER" | "EMPLOYEE";
  isPrimary: boolean;
  createdAt: string;
  legalEntity: {
    id: string;
    displayName: string;
    inn: string;
    kpp: string | null;
    ogrn: string | null;
    legalAddress: string;
    taxSystem: AdminTaxSystem | null;
    vatRequired: boolean;
    contactPhone: string | null;
    contactEmail: string | null;
    isActive: boolean;
    bankAccounts: CustomerBankAccountDto[];
  };
}

export const customerLegalEntities = {
  list: (customerId: string) =>
    apiFetch<CustomerLegalEntityLinkDto[]>(
      `/admin/customers/${customerId}/legal-entities`,
    ),

  attach: (customerId: string, legalEntityId: string, isPrimary = false) =>
    apiFetch<{ id: string }>(
      `/admin/customers/${customerId}/legal-entities/${legalEntityId}/attach`,
      { method: "POST", json: { isPrimary } },
    ),

  setPrimary: (customerId: string, legalEntityId: string) =>
    apiFetch<{ id: string }>(
      `/admin/customers/${customerId}/legal-entities/${legalEntityId}/set-primary`,
      { method: "POST" },
    ),

  detach: (customerId: string, legalEntityId: string) =>
    apiFetch<void>(
      `/admin/customers/${customerId}/legal-entities/${legalEntityId}`,
      { method: "DELETE" },
    ),
};

export const TAX_SYSTEM_LABEL: Record<AdminTaxSystem, string> = {
  OSNO: "ОСНО (с НДС)",
  USN6: "УСН 6%",
  USN15: "УСН 15%",
  PSN: "Патент",
  NPD: "Самозанятые",
  ESHN: "ЕСХН",
};

// ─── LegalEntities ────────────────────────────────────────────────────────────

export const legalEntities = {
  list: () => apiFetch<LegalEntityDto[]>("/admin/legal-entities"),

  findOne: (id: string) => apiFetch<LegalEntityDto>(`/admin/legal-entities/${id}`),

  lookupByInn: (inn: string) =>
    apiFetch<{
      found: boolean;
      suggested?: {
        displayName: string;
        inn: string;
        kpp: string | null;
        ogrn: string | null;
        legalAddress: string;
        type?: string;
        state?: string;
      };
    }>(`/admin/legal-entities/by-inn?inn=${encodeURIComponent(inn)}`),

  create: (payload: Omit<LegalEntityDto, "id" | "isActive" | "dadataSyncedAt" | "createdAt" | "updatedAt"> & { dadataSynced?: boolean }) =>
    apiFetch<LegalEntityDto>("/admin/legal-entities", {
      method: "POST",
      json: payload,
    }),

  update: (id: string, payload: Partial<LegalEntityDto>) =>
    apiFetch<LegalEntityDto>(`/admin/legal-entities/${id}`, {
      method: "PATCH",
      json: payload,
    }),

  remove: (id: string, force = false) =>
    apiFetch<{ deleted: boolean; deactivated: boolean }>(
      `/admin/legal-entities/${id}${force ? "?force=true" : ""}`,
      { method: "DELETE" },
    ),
};

// ─── Addresses ────────────────────────────────────────────────────────────────

export const addresses = {
  listForCustomer: (customerId: string) =>
    apiFetch<AddressDto[]>(`/admin/customers/${customerId}/addresses`),

  suggest: (q: string, limit?: number) => {
    const params = new URLSearchParams({ q });
    if (limit) params.set("limit", String(limit));
    return apiFetch<{ suggestions: unknown[] }>(
      `/admin/addresses/suggest?${params.toString()}`,
    );
  },

  create: (customerId: string, payload: Omit<AddressDto, "id" | "customerId" | "createdAt" | "updatedAt" | "dadataSyncedAt"> & { dadataSynced?: boolean }) =>
    apiFetch<AddressDto>(`/admin/customers/${customerId}/addresses`, {
      method: "POST",
      json: payload,
    }),

  update: (id: string, payload: Partial<AddressDto>) =>
    apiFetch<AddressDto>(`/admin/addresses/${id}`, {
      method: "PATCH",
      json: payload,
    }),

  remove: (id: string) =>
    apiFetch<void>(`/admin/addresses/${id}`, { method: "DELETE" }),
};

// ─── Privacy: Consents / Blocks / DeletionRequests / PolicyVersions ───────────

export const consents = {
  list: (customerId: string) =>
    apiFetch<ConsentRecordDto[]>(`/admin/customers/${customerId}/consents`),

  /**
   * Согласия по видам (шаг Б «Юридических документов»): виды с историей;
   * прежний плоский ответ раскладывается `groupConsents`.
   */
  listGrouped: (customerId: string) =>
    apiFetch<Array<ConsentRecordDto | ConsentKindDto>>(`/admin/customers/${customerId}/consents`).then(groupConsents),

  grant: (
    customerId: string,
    payload: {
      consentType: ConsentType;
      policyVersionId: string;
      source: ConsentSource;
    },
  ) =>
    apiFetch<ConsentRecordDto>(`/admin/customers/${customerId}/consents`, {
      method: "POST",
      json: payload,
    }),

  revoke: (consentId: string) =>
    apiFetch<ConsentRecordDto>(`/admin/consents/${consentId}/revoke`, {
      method: "POST",
    }),
};

export const blocks = {
  list: (customerId: string) =>
    apiFetch<CustomerBlockDto[]>(`/admin/customers/${customerId}/blocks`),

  block: (
    customerId: string,
    payload: {
      reason: string;
      scope: CustomerBlockScope;
      expiresAt?: string;
    },
  ) =>
    apiFetch<CustomerBlockDto>(`/admin/customers/${customerId}/block`, {
      method: "POST",
      json: payload,
    }),

  unblock: (blockId: string, revokedReason: string) =>
    apiFetch<CustomerBlockDto>(`/admin/blocks/${blockId}/unblock`, {
      method: "POST",
      json: { revokedReason },
    }),
};

export const deletionRequests = {
  listForCustomer: (customerId: string) =>
    apiFetch<DataDeletionRequestDto[]>(
      `/admin/customers/${customerId}/deletion-requests`,
    ),

  listAll: (status?: DataDeletionStatus) =>
    apiFetch<DataDeletionRequestDto[]>(
      `/admin/deletion-requests${status ? `?status=${status}` : ""}`,
    ),

  create: (
    customerId: string,
    payload: { requestSource?: string; notes?: string },
  ) =>
    apiFetch<DataDeletionRequestDto>(
      `/admin/customers/${customerId}/deletion-requests`,
      { method: "POST", json: payload },
    ),

  complete: (requestId: string) =>
    apiFetch<DataDeletionRequestDto>(
      `/admin/deletion-requests/${requestId}/complete`,
      { method: "POST" },
    ),

  reject: (requestId: string, rejectionReason: string) =>
    apiFetch<DataDeletionRequestDto>(
      `/admin/deletion-requests/${requestId}/reject`,
      { method: "POST", json: { rejectionReason } },
    ),
};

export const policyVersions = {
  list: () => apiFetch<PolicyVersionDto[]>("/admin/policy-versions"),

  publish: (payload: { policyKind: string; version: string; content: string }) =>
    apiFetch<PolicyVersionDto>("/admin/policy-versions", {
      method: "POST",
      json: payload,
    }),
};
